import { faSearch, faChevronDown, faChevronRight, faBug } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import Tooltip from '@/ui/components/Tooltip';

import { AlertGroupSummary, AlertMessageCount } from '../types';
import { titleize } from '../utils/titleize';

/**
 * Renders alert counts grouped by field. Driven by the normalized
 * AlertGroupSummary model, so the same list works whether the counts were
 * aggregated client-side from loaded rows (BillingReportErrorSummary) or come
 * from the billing items summary endpoint, which counts the whole filtered set.
 *
 * The search callbacks receive the whole message rather than just its text, so a
 * server-aggregated caller can filter by the stable BillingAlert id while the
 * client-side one still filters by message text. Each renders only if given:
 * the client-side panel counts billed items but can't filter them out, so it
 * offers `searchUnbilled` as a separate action, while a server-aggregated panel
 * has already excluded them from the count and needs only `search`.
 */
const AlertSummaryList = ({
  groups,
  type,
  search,
  searchUnbilled
}: {
  groups: AlertGroupSummary[];
  type: 'error' | 'warning';
  search?: (message: AlertMessageCount) => void;
  searchUnbilled?: (message: AlertMessageCount) => void;
}) => {
  // State to keep track of which li elements are expanded or collapsed
  const [expandedFields, setExpandedFields] = useState<string[]>([]);

  if (groups.length === 0) {
    return (
      <div className="p-4 bg-white border border-gray-300 shadow">
        <h2 className="text-lg font-bold">Horray!</h2>
        <p className="text-xs text-gray-500">No {type}s were found.</p>
      </div>
    );
  }

  return (
    <ul>
      {groups.map(({ group, count: totalCount, messages }) => (
        <li key={group}>
          <button
            className="text-md font-bold cursor-pointer"
            onClick={() =>
              setExpandedFields((prev) =>
                prev.includes(group) ? prev.filter((f) => f !== group) : [...prev, group]
              )
            }>
            {expandedFields.includes(group) ? (
              <FontAwesomeIcon icon={faChevronDown} className="mr-1" />
            ) : (
              <FontAwesomeIcon icon={faChevronRight} className="mr-1" />
            )}
            {titleize(group)} ({totalCount})
          </button>
          {expandedFields.includes(group) && (
            <ul className="text-sm ml-4">
              {messages.map((message) => (
                <li key={message.description} className="cursor-pointer">
                  <div className="flex items-center">
                    <strong>({message.count})</strong>: {message.description}
                    {search && (
                      <Tooltip tooltipContent="Search for this">
                        <button
                          className="btn btn-sm btn-default ml-1"
                          onClick={() => search(message)}>
                          <FontAwesomeIcon icon={faSearch} />
                        </button>
                      </Tooltip>
                    )}
                    {searchUnbilled && (
                      <Tooltip tooltipContent="Show only unbilled items with this">
                        <button
                          className="btn btn-sm btn-default ml-1"
                          onClick={() => searchUnbilled(message)}>
                          <FontAwesomeIcon icon={faBug} color="red" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
};

export default AlertSummaryList;
